import { Component } from '@theme/component';

/**
 * Interactive creative editor demo on the "24 Creatives" landing page (sections/c24-editor.liquid).
 * Layers can be selected on the canvas or in the layer list, edited through the panel, dragged with
 * the pointer and nudged with the arrow keys.
 *
 * @typedef {object} EditorRefs
 * @property {HTMLElement} doc - The creative canvas
 * @property {HTMLElement} selection - Selection frame drawn around the selected layer
 * @property {HTMLElement} selectionLabel - Name tag of the selection frame
 * @property {HTMLElement} props - Property panel
 * @property {HTMLElement} [status] - Save status in the window bar
 * @property {HTMLElement} textField - Text field wrapper
 * @property {HTMLTextAreaElement} textInput - Text of the selected layer
 * @property {HTMLElement} fontField - Font field wrapper
 * @property {HTMLElement} sizeField - Size field wrapper
 * @property {HTMLInputElement} sizeInput - Scale of the selected layer in percent
 * @property {HTMLElement[]} layers - Editable layers on the canvas
 * @property {HTMLButtonElement[]} layerButtons - Layer list buttons
 * @property {HTMLInputElement[]} swatches - Colour radios
 * @property {HTMLInputElement[]} fonts - Font radios
 *
 * @extends {Component<EditorRefs>}
 */
class C24Editor extends Component {
  requiredRefs = ['doc', 'selection', 'selectionLabel', 'props', 'textInput', 'sizeInput'];

  /** @type {AbortController | undefined} */
  #abort;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {HTMLElement | null} */
  #selected = null;
  /** @type {{ layer: HTMLElement, pointerId: number, startX: number, startY: number, x: number, y: number, moved: boolean } | null} */
  #drag = null;
  /** @type {{ layers: { el: HTMLElement, style: string, html: string, font: string | undefined }[] } | undefined} */
  #initial;
  /** @type {number | undefined} */
  #saveTimer;

  connectedCallback() {
    super.connectedCallback();

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#initial = {
      layers: this.refs.layers.map((el) => ({
        el,
        style: el.getAttribute('style') || '',
        html: el.querySelector('[data-text]')?.innerHTML || '',
        font: el.dataset.font,
      })),
    };

    const { doc } = this.refs;
    doc.addEventListener('pointerdown', this.#onPointerDown, { signal });
    doc.addEventListener('pointermove', this.#onPointerMove, { signal });
    doc.addEventListener('pointerup', this.#onPointerUp, { signal });
    doc.addEventListener('pointercancel', this.#onPointerUp, { signal });
    doc.addEventListener('keydown', this.#onKeyDown, { signal });

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.#syncSelection());
    this.#resizeObserver.observe(doc);

    // Start with the headline selected so the panel shows what can be edited.
    const headline = this.#layer('headline');
    if (headline) this.#select(headline);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    window.clearTimeout(this.#saveTimer);
  }

  /**
   * Selects the layer that belongs to a layer list button.
   * @param {Event} event
   */
  onLayerButton(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const layer = this.#layer(button.dataset.layer);
    if (!layer) return;
    this.#select(layer);
    layer.focus({ preventScroll: true });
  }

  /**
   * Writes the panel text into the selected layer. Single-line layers ignore line breaks.
   * @param {Event} event
   */
  onText(event) {
    const input = event.target;
    const layer = this.#selected;
    if (!(input instanceof HTMLTextAreaElement) || !layer) return;
    const target = layer.querySelector('[data-text]');
    if (!target) return;

    const multiline = layer.dataset.layer === 'headline';
    const lines = (multiline ? input.value.split('\n') : [input.value.replace(/\n/g, ' ')]).slice(0, 4);
    target.replaceChildren(
      ...lines.flatMap((line, index) => (index === 0 ? [line] : [document.createElement('br'), line]))
    );
    this.#changed();
  }

  /**
   * Applies a colour: text colour for text layers, fill for shapes (with a readable text colour).
   * @param {Event} event
   */
  onColor(event) {
    const input = event.target;
    const layer = this.#selected;
    if (!(input instanceof HTMLInputElement) || !layer) return;
    if (layer.dataset.kind === 'shape') {
      layer.style.setProperty('--bg', input.value);
      layer.style.setProperty('--fg', isLight(input.value) ? '#151515' : '#ffffff');
    } else {
      layer.style.setProperty('--c', input.value);
    }
    this.#changed();
  }

  /**
   * Switches the headline between the sans and the serif font.
   * @param {Event} event
   */
  onFont(event) {
    const input = event.target;
    const layer = this.#selected;
    if (!(input instanceof HTMLInputElement) || !layer) return;
    layer.dataset.font = input.value;
    this.#changed();
  }

  /**
   * Scales the selected layer.
   * @param {Event} event
   */
  onSize(event) {
    const input = event.target;
    const layer = this.#selected;
    if (!(input instanceof HTMLInputElement) || !layer) return;
    layer.style.setProperty('--scale', String(Number(input.value) / 100));
    this.#changed();
  }

  /** Restores the creative as it was rendered. */
  reset() {
    if (!this.#initial) return;
    for (const { el, style, html, font } of this.#initial.layers) {
      el.setAttribute('style', style);
      const target = el.querySelector('[data-text]');
      if (target) target.innerHTML = html;
      if (font) el.dataset.font = font;
    }
    const headline = this.#layer('headline');
    if (headline) this.#select(headline);
    this.#changed();
  }

  /** @param {string | undefined} name */
  #layer(name) {
    return this.refs.layers.find((layer) => layer.dataset.layer === name) || null;
  }

  /** @param {HTMLElement | null} layer */
  #select(layer) {
    this.#selected = layer;

    for (const el of this.refs.layers) el.classList.toggle('is-selected', el === layer);
    for (const button of this.refs.layerButtons || []) {
      button.setAttribute('aria-pressed', String(!!layer && button.dataset.layer === layer.dataset.layer));
    }

    const { props, textInput, sizeInput, textField, fontField } = this.refs;
    props.classList.toggle('has-selection', !!layer);
    // Fields stay in place and are only switched off, so the panel keeps its height.
    for (const field of props.querySelectorAll('.c24-ed__field')) {
      if (field instanceof HTMLElement) setAvailable(field, !!layer);
    }

    if (!layer) textInput.value = '';

    if (layer) {
      const target = layer.querySelector('[data-text]');
      textInput.value = target instanceof HTMLElement ? target.innerText.trim() : '';
      setAvailable(textField, !!target);

      const isShape = layer.dataset.kind === 'shape';
      const color = (layer.style.getPropertyValue(isShape ? '--bg' : '--c') || '').trim().toLowerCase();
      for (const swatch of this.refs.swatches || []) swatch.checked = swatch.value.toLowerCase() === color;

      const hasFont = layer.dataset.layer === 'headline';
      setAvailable(fontField, hasFont);
      for (const radio of this.refs.fonts || []) radio.checked = radio.value === (layer.dataset.font || 'sans');

      const scale = Number(layer.style.getPropertyValue('--scale')) || 1;
      sizeInput.value = String(Math.round(scale * 100));

      this.refs.selectionLabel.textContent = layer.dataset.label || '';
    }

    this.#syncSelection();
  }

  /** Draws the selection frame around the selected layer. */
  #syncSelection() {
    const { selection, doc } = this.refs;
    const layer = this.#selected;
    if (!layer) {
      selection.hidden = true;
      return;
    }
    const docRect = doc.getBoundingClientRect();
    const rect = layer.getBoundingClientRect();
    const pad = 4;
    selection.hidden = false;
    selection.style.setProperty('--sx', `${rect.left - docRect.left - pad}px`);
    selection.style.setProperty('--sy', `${rect.top - docRect.top - pad}px`);
    selection.style.setProperty('--sw', `${rect.width + pad * 2}px`);
    selection.style.setProperty('--sh', `${rect.height + pad * 2}px`);
    selection.classList.toggle('is-below', rect.top - docRect.top < 28);
  }

  #changed() {
    this.#syncSelection();
    const { status } = this.refs;
    if (!status) return;
    status.textContent = this.dataset.labelSaving || '';
    status.classList.add('is-saving');
    window.clearTimeout(this.#saveTimer);
    this.#saveTimer = window.setTimeout(() => {
      status.textContent = this.dataset.labelSaved || '';
      status.classList.remove('is-saving');
    }, 700);
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button !== 0) return;
    const target = event.target instanceof Element ? event.target.closest('[data-layer]') : null;
    const layer = target instanceof HTMLElement && this.refs.layers.includes(target) ? target : null;

    if (!layer) {
      this.#select(null);
      return;
    }

    const wasSelected = layer === this.#selected;
    this.#select(layer);

    // On touch, the first tap only selects; dragging starts once the layer is selected.
    if (event.pointerType === 'touch' && !wasSelected) return;

    event.preventDefault();
    layer.focus({ preventScroll: true });
    this.refs.doc.setPointerCapture(event.pointerId);
    this.#drag = {
      layer,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      x: parseFloat(layer.style.getPropertyValue('--x')) || 0,
      y: parseFloat(layer.style.getPropertyValue('--y')) || 0,
      moved: false,
    };
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const rect = this.refs.doc.getBoundingClientRect();
    const dx = ((event.clientX - drag.startX) / rect.width) * 100;
    const dy = ((event.clientY - drag.startY) / rect.height) * 100;
    if (!drag.moved && Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 3) return;
    if (!drag.moved) {
      drag.moved = true;
      this.classList.add('is-dragging');
    }
    this.#place(drag.layer, drag.x + dx, drag.y + dy);
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const drag = this.#drag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (this.refs.doc.hasPointerCapture(event.pointerId)) this.refs.doc.releasePointerCapture(event.pointerId);
    this.#drag = null;
    this.classList.remove('is-dragging');
    if (drag.moved) this.#changed();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const layer = event.target instanceof HTMLElement && this.refs.layers.includes(event.target) ? event.target : null;
    if (!layer) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#select(layer);
      return;
    }

    if (event.key === 'Escape') {
      this.#select(null);
      return;
    }

    /** @type {Record<string, number[]>} */
    const moves = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    if (layer !== this.#selected) this.#select(layer);
    const step = event.shiftKey ? 5 : 1;
    const x = parseFloat(layer.style.getPropertyValue('--x')) || 0;
    const y = parseFloat(layer.style.getPropertyValue('--y')) || 0;
    this.classList.add('is-dragging');
    this.#place(layer, x + move[0] * step, y + move[1] * step);
    this.classList.remove('is-dragging');
    this.#changed();
  };

  /**
   * @param {HTMLElement} layer
   * @param {number} x - Left edge in percent of the canvas width
   * @param {number} y - Top edge in percent of the canvas height
   */
  #place(layer, x, y) {
    const clamp = (/** @type {number} */ value) => Math.min(96, Math.max(-4, value));
    layer.style.setProperty('--x', `${clamp(x).toFixed(1)}%`);
    layer.style.setProperty('--y', `${clamp(y).toFixed(1)}%`);
    this.#syncSelection();
  }
}

/**
 * Whether a hex colour is light enough to need dark text on it.
 * @param {string} hex
 */
function isLight(hex) {
  const value = hex.replace('#', '');
  if (value.length !== 6) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16) / 255);
  const lin = (/** @type {number} */ c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b) > 0.45;
}

/**
 * Switches a panel field on or off without taking it out of the layout.
 * @param {HTMLElement | undefined} field
 * @param {boolean} on
 */
function setAvailable(field, on) {
  if (!field) return;
  field.inert = !on;
  field.classList.toggle('is-off', !on);
}

if (!customElements.get('c24-editor')) customElements.define('c24-editor', C24Editor);
